import { makeStyles } from "@mui/styles";
import { Box, Card, Typography, Tabs, Tab, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { ClassExtended, TeacherClassDetailsCourse } from "../../../interfaces";
import { LessonPlanModal } from "./LessonPlanModal";
import { getClassesFeedback, HandleDataLayer } from "../../../Helpers";
import { FeedbackSummary } from "../../v2/insights/Summary/HintsAndErrorView";

const useStyle = makeStyles((theme) => ({
  tabs: {
    borderBottom: `2px solid ${theme.palette.divider}`,
    "& .MuiTab-root": {
      color: theme.palette.text.primary,
      fontSize: 16,
      fontWeight: 600,
      "&:hover": {
        boxSizing: "border-box",
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        borderTop: `2px solid transparent`,
        backgroundColor: "#FAFAFA",
      },
    },
    "& .MuiTabs-indicator": {
      height: "4px",
    },
  },
  tab: {
    height: 43,
  },
  learningTargetCard: {
    padding: "16px",
    width: "100%",
    borderRadius: "8px",
    backgroundColor: theme.palette.divider,
    border: `1px solid transparent`,
    cursor: "pointer",
    "&:hover": {
      border: `1px solid ${theme.palette.primary.main}`,
      "& .title": {
        textDecoration: "underline",
      },
    },
  },
  learningTargetCardNum: {
    marginRight: "16px",
    width: "40px",
    height: "40px",
    aspectRatio: "1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    backgroundColor: "#80449F",
  },
}));

interface LessonPlansCardProps {
  containerId?: string;
  classesExtended?: ClassExtended[];
  teacherCourses: TeacherClassDetailsCourse[] | null;
}

export const LessonPlansCard = (props: LessonPlansCardProps) => {
  const { containerId, classesExtended, teacherCourses } = props;
  const classes = useStyle();
  const [courseSelected, setCourseSelected] = useState<number | null>(null);
  const [targetSelected, setTargetSelected] = useState<string | null>(null);
  const modalOpen = Boolean(targetSelected);

  const [loadedData, setLoadedData] = useState<FeedbackSummary | null>(null);
  const [classList, setClassList] = useState<ClassExtended[] | null>(null);

  const tabs =
    teacherCourses?.map((course, index) => {
      return {
        id: course.course_id,
        label: course.course_name,
        key: `tab-${course.course_name}`,
        htmlId: `tab-${course.course_name}`,
      };
    }) ?? [];

  const handleOpenLessonPlan = (targetId: string, label: string) => {
    setTargetSelected(targetId);
    HandleDataLayer(
      "modal",
      "open",
      "click",
      "main content",
      tabs.filter((tab) => tab.id === courseSelected)[0].label,
      label,
      "onclick",
      "Skill review recommendations"
    );
  };

  const handleChangeTab = (courseId: number, tabLabel: string) => {
    if (courseSelected !== courseId) {
      setLoadedData(null);
    }
    HandleDataLayer(
      "link",
      "click",
      "internal link",
      "main content",
      "Skill review recommendations",
      tabLabel,
      "onclick"
    );
    setCourseSelected(courseId);
  };

  useEffect(() => {
    if (!courseSelected && teacherCourses && teacherCourses.length > 0) {
      if (courseSelected !== teacherCourses[0].course_id) {
        setCourseSelected(teacherCourses[0].course_id);
      }
    }
  }, [teacherCourses, courseSelected]);

  useEffect(() => {
    if (classesExtended && !classList) {
      setClassList(classesExtended);
    }
  }, [classList, classesExtended]);

  useEffect(() => {
    if (!classList || !courseSelected) return;
    let isCancelled = false;

    // filter feedback lookup to just the classes in current course
    const filteredClassList = classList.filter(
      (item) => item.course_id === courseSelected
    );

    const fetchFeedbackData = async () => {
      if (!loadedData) {
        try {
          const course = teacherCourses?.find(
            (item) => item.course_id === courseSelected
          );
          if (!course) return;
          let data = await getClassesFeedback(filteredClassList, course?.course_modules);
          if (!isCancelled) {
            setLoadedData(data);
          }
        } catch (err) {
          console.error("An unexpected error occurred:", err);
        }
      }
    };

    fetchFeedbackData();

    return () => {
      isCancelled = true;
    };
  }, [classList, loadedData, courseSelected, teacherCourses]);

  const noData = (
    <Typography variant="body2" component="div">
      Learning targets that students across all your classes are struggling with the most
      on average will be displayed here
    </Typography>
  );

  return (
    <>
      <div id={containerId}></div>
      <Card
        sx={{
          marginBottom: "24px",
          padding: "2rem",
        }}>
        <Typography
          variant="h4"
          component="div"
          sx={{ width: "100%", marginBottom: "12px" }}>
          Skill review recommendations
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{ width: "100%", marginBottom: "16px" }}>
          Top 3 targets with most hints and errors
        </Typography>
        {!classList && !teacherCourses ? (
          noData
        ) : (
          <>
            <Box sx={{ width: "100%", marginBottom: "16px" }}>
              <Tabs
                className={classes.tabs}
                value={
                  courseSelected
                    ? tabs.map((tab) => tab.id).indexOf(courseSelected)
                    : false
                }
                aria-label="lesson-plan-tabs">
                {tabs.length &&
                  tabs.map((tab, index) => {
                    return (
                      <Tab
                        value={index}
                        label={tab.label}
                        key={tab.key}
                        id={tab.htmlId}
                        className={classes.tab}
                        onClick={() => handleChangeTab(tab.id, tab.label)}
                      />
                    );
                  })}
              </Tabs>
            </Box>
            <Stack direction="column" gap="8px">
              {loadedData &&
              loadedData.topTargetFeedback &&
              loadedData?.topTargetFeedback?.length > 0
                ? loadedData?.topTargetFeedback
                    ?.slice(0, 3)
                    .map((learningTarget, index) => {
                      return (
                        <Stack
                          onClick={() =>
                            handleOpenLessonPlan(
                              learningTarget.targetId,
                              learningTarget.targetName
                            )
                          }
                          key={`recommendation-${learningTarget.targetId}`}
                          className={classes.learningTargetCard}
                          direction="row">
                          <Box className={classes.learningTargetCardNum}>
                            <Typography
                              variant="h4"
                              fontSize="18px"
                              fontWeight="700"
                              color="white">
                              {index + 1}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            lineHeight="21px"
                            className={`title`}>
                            {learningTarget.targetName}
                          </Typography>
                        </Stack>
                      );
                    })
                : noData}
            </Stack>
          </>
        )}
      </Card>
      <LessonPlanModal
        open={modalOpen}
        targetId={targetSelected}
        loadedData={loadedData}
        classList={classList}
        handleClose={() => setTargetSelected(null)}
      />
    </>
  );
};
